import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { authConfig } from '../../config/auth';
import { userManager, User } from '../../utils/auth';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import LandingNav from '../LandingNav';
import '../../styles/auth.css';
import '../../styles/landing.css';

/**
 * Invite landing page. New users arrive here from the "set your password" email
 * with their email + one-time code pre-filled in the URL. Setting a password
 * redeems the code (ConfirmForgotPassword under the hood) and auto-logs them in.
 *
 * If the code has expired (codes are valid ~1 hour), we don't dead-end them:
 * we explain it and send them to the standard password-recovery flow, which
 * works because the account already exists as a confirmed user.
 */
export default function Welcome() {
  usePageTitle('Set Your Password');
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();

  const email = (searchParams.get('email') || '').toLowerCase().trim();
  const code = (searchParams.get('code') || '').trim();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(false);

  const fetchUserProfile = async (idToken: string): Promise<User | null> => {
    try {
      const response = await fetch(`${authConfig.apiUrl}/api/profile`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.user ?? null;
    } catch {
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      // 1. Redeem the invite code and set the chosen password.
      const resetResp = await fetch(`${authConfig.apiUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, newPassword }),
      });
      const resetData = await resetResp.json();

      if (!resetResp.ok) {
        // Expired / invalid / already-used code: route them to recovery instead
        // of leaving them stuck on a broken link.
        if (
          resetData.error === 'ExpiredCodeException' ||
          resetData.error === 'CodeMismatchException'
        ) {
          setExpired(true);
          setLoading(false);
          return;
        }
        setError(resetData.message || 'Failed to set your password');
        setLoading(false);
        return;
      }

      // 2. Log them straight in with the password they just set.
      const loginResp = await fetch(`${authConfig.apiUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: newPassword }),
      });
      const loginData = await loginResp.json();

      if (loginResp.ok && loginData.authenticationResult) {
        const tokens = {
          accessToken: loginData.authenticationResult.AccessToken,
          idToken: loginData.authenticationResult.IdToken,
          refreshToken: loginData.authenticationResult.RefreshToken,
          expiresIn: loginData.authenticationResult.ExpiresIn,
          expiresAt: Date.now() + loginData.authenticationResult.ExpiresIn * 1000,
        };
        const user = await fetchUserProfile(tokens.idToken);
        login(tokens, user || ({} as User));
        navigate('/dashboard');
        return;
      }

      // Password was set but auto-login failed for some reason — send them to login.
      navigate('/login');
    } catch {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  // Missing params → not a valid invite link.
  const invalidLink = !email || !code;

  return (
    <div className="landing-page">
      <LandingNav />

      <div className="auth-container">
        <div className="auth-header">
          <p className="auth-header-subtitle">Welcome to CrewCast</p>
        </div>

        <div className="auth-card">
          {expired || invalidLink ? (
            <>
              <div className="success-icon" style={{ marginBottom: '12px' }}>
                <AlertCircle size={32} color="#ffb020" />
              </div>
              <h2 className="auth-card-title">
                {invalidLink ? 'Invalid invite link' : 'This link has expired'}
              </h2>
              <p className="text-center info-text" style={{ marginBottom: '24px' }}>
                {invalidLink
                  ? 'This link is missing some information. Please use the link from your invite email, or request a new one below.'
                  : 'Invite links expire after a short time for security. No problem — request a fresh code and set your password.'}
              </p>
              <button
                type="button"
                onClick={() =>
                  navigate('/forgot-password', { state: { email: email || undefined } })
                }
                className="auth-button"
              >
                Get a new link
              </button>
            </>
          ) : (
            <>
              <h2 className="auth-card-title">Set Your Password</h2>
              <p className="text-center info-text" style={{ marginBottom: '24px' }}>
                Choose a password to finish setting up your account.
              </p>

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="input-container">
                  <span className="input-icon">
                    <Lock size={18} />
                  </span>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="New password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-field"
                    required
                    minLength={8}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="toggle-password-btn"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                <div className="input-container">
                  <span className="input-icon">
                    <Lock size={18} />
                  </span>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-field"
                    required
                    minLength={8}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="toggle-password-btn"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                <div className="hint-text">
                  Password must be at least 8 characters with uppercase, lowercase, and numbers
                </div>

                {error && <div className="error-message">{error}</div>}

                <button type="submit" disabled={loading} className="auth-button">
                  {loading ? 'Setting Password...' : 'Set Password & Continue'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
